import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import API from "@/lib/api";
import { CATEGORIES, STONES } from "@/lib/data";
import { FadeUp } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "all";
  const stone = params.get("stone") || "all";

  useEffect(() => {
    API.get("/products")
      .then((res) => setProducts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () =>
      products.filter(
        (p) =>
          (active === "all" || p.category === active) &&
          (stone === "all" || p.stone === stone)
      ),
    [products, active, stone]
  );

  const counts = useMemo(() => {
    const c = { all: products.length };
    products.forEach((p) => (c[p.category] = (c[p.category] || 0) + 1));
    return c;
  }, [products]);

  const stoneCounts = useMemo(() => {
    const s = { all: products.filter((p) => p.stone).length };
    products.forEach((p) => {
      if (p.stone) s[p.stone] = (s[p.stone] || 0) + 1;
    });
    return s;
  }, [products]);

  const setCategory = (value) => {
    const next = {};
    if (value !== "all") next.category = value;
    if (stone !== "all") next.stone = stone;
    setParams(next);
  };

  const setStone = (value) => {
    const next = {};
    if (active !== "all") next.category = active;
    if (value !== "all") next.stone = value;
    setParams(next);
  };

  const pillCls = (isActive) =>
    `rounded-full px-5 py-2.5 text-[12px] uppercase tracking-widest2 border transition-colors duration-300 ${
      isActive
        ? "bg-gold text-ink border-gold font-semibold"
        : "border-line text-mist hover:border-gold/50 hover:text-gold"
    }`;

  const stonePillCls = (isActive) =>
    `rounded-full px-5 py-2.5 text-[12px] uppercase tracking-widest2 border transition-colors duration-300 ${
      isActive
        ? "bg-sage text-ink border-sage font-semibold"
        : "border-line text-mist hover:border-sage/60 hover:text-sage"
    }`;

  return (
    <main className="pt-16">
      <section data-testid="shop-page" className="mx-auto max-w-7xl px-5 sm:px-8 py-16 md:py-24">
        <FadeUp>
          <p className="text-[12px] uppercase tracking-widest2 text-gold">The Cabinet</p>
          <h1 className="mt-4 font-display font-light tracking-tighter text-[clamp(2.8rem,6vw,5.5rem)] leading-none">
            Everything on the <span className="italic text-gold">shelves</span>
          </h1>
          <p className="mt-4 text-sm text-mist/60 italic font-display text-base" data-testid="shop-price-note">
            *Prices may vary according to the stone.
          </p>
        </FadeUp>

        <div className="mt-12 space-y-4" data-testid="shop-filter-bar">
          <div className="flex flex-wrap gap-3">
            <button data-testid="filter-pill-all" onClick={() => setCategory("all")} className={pillCls(active === "all")}>
              All &middot; {counts.all || 0}
            </button>
            {CATEGORIES.map((c) => (
              <button key={c.key} data-testid={`filter-pill-${c.key}`} onClick={() => setCategory(c.key)} className={pillCls(active === c.key)}>
                {c.label} &middot; {counts[c.key] || 0}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[11px] uppercase tracking-widest2 text-sage/70 mr-1">By stone</span>
            <button data-testid="stone-pill-all" onClick={() => setStone("all")} className={stonePillCls(stone === "all")}>
              All stones
            </button>
            {STONES.map((s) => (
              <button key={s.key} data-testid={`stone-pill-${s.key}`} onClick={() => setStone(s.key)} className={stonePillCls(stone === s.key)}>
                {s.label} &middot; {stoneCounts[s.key] || 0}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="shop-loading">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] rounded-xl bg-surface border border-line animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-24 text-center py-20 border border-dashed border-line rounded-xl" data-testid="shop-empty-state">
            <p className="font-display text-3xl text-mist italic">The shelf is bare here.</p>
            <p className="mt-3 text-sm text-mist/70">The shelves restock with the new moon — check back soon.</p>
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-10" data-testid="shop-product-grid">
            {filtered.map((p, i) => (
              <FadeUp key={p.id} delay={Math.min(i, 6) * 0.05}>
                <ProductCard product={p} index={i} />
              </FadeUp>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
